import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import InstallPWA from './components/common/InstallPWA';
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';
import MemberLayout from './layouts/MemberLayout';
import CompteRenduForm from './components/admin/CompteRenduForm';
import CompteRenduListe from './components/admin/CompteRenduListe';
import DebugComptesRendus from './pages/admin/DebugComptesRendus';
import CompteRenduDetail from './components/admin/CompteRenduDetail';
import ValidationMembres from './components/admin/ValidationMembres';
import ValidationAnnonces from './components/admin/ValidationAnnonces';


// Lazy loading des pages
const HomePage = lazy(() => import('./pages/public/HomePage'));
const BlogPage = lazy(() => import('./pages/public/BlogPage'));
const BlogPostPage      = lazy(() => import('./pages/public/BlogPostPage'));
const PrivacyPage       = lazy(() => import('./pages/public/PrivacyPage'));
const TermsPage         = lazy(() => import('./pages/public/TermsPage'));
const HelpPage          = lazy(() => import('./pages/public/HelpPage'));
const DeleteAccountPage = lazy(() => import('./pages/public/DeleteAccountPage'));
const ArbreGenealogique = lazy(() => import('./pages/genealogie/ArbreGenealogique'));
const AnnuairePage      = lazy(() => import('./pages/public/Annuaire'));
const CoinAffairesPage = lazy(() => import('./pages/public/CoinAffaires'));
const AboutPage = lazy(() => import('./pages/public/About'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));

// Pages Admin
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminMembres = lazy(() => import('./pages/admin/Membres'));
const AdminCotisations = lazy(() => import('./pages/admin/Cotisations'));
const AdminAssemblees = lazy(() => import('./pages/admin/Assemblees'));
const AdminTontine = lazy(() => import('./pages/admin/Tontine'));
const AdminAides = lazy(() => import('./pages/admin/Aides'));
const AdminBlog = lazy(() => import('./pages/admin/Blog'));
const AdminDocumentsOfficiels = lazy(() => import('./pages/admin/DocumentsOfficiels'));

// const AdminComptesRendus = lazy(() => import('./pages/admin/ComptesRendus')); // Ancien composant
const AdminDepenses = lazy(() => import('./pages/admin/Depenses'));
const AdminRecettes = lazy(() => import('./pages/admin/Recettes'));
const AdminRapports = lazy(() => import('./pages/admin/Rapports'));
const AdminDefaillants = lazy(() => import('./pages/admin/Defaillants'));
const AdminSettings    = lazy(() => import('./pages/admin/Settings'));
const AdminRappels       = lazy(() => import('./components/admin/RappelsAssemblee'));
const AdminMessagerie    = lazy(() => import('./components/admin/MessagerieWhatsApp'));
const AdminAnniversaires = lazy(() => import('./components/admin/Anniversaires'));
const AdminEmprunts      = lazy(() => import('./components/admin/Emprunts'));
const AdminEvenements    = lazy(() => import('./components/admin/Evenements'));
const GenealogiAdmin     = lazy(() => import('./components/admin/GenealogiAdmin'));
const AdminComptesBancaires = lazy(() => import('./pages/admin/ComptesBancaires'));

// Pages Membre
const MemberDashboard = lazy(() => import('./pages/member/Dashboard'));
const MemberProfile = lazy(() => import('./pages/member/Profile'));
const MemberCotisations = lazy(() => import('./pages/member/Cotisations'));
const MemberAssemblees = lazy(() => import('./pages/member/Assemblees'));
const MemberTontine = lazy(() => import('./pages/member/Tontine'));
const MemberDocuments = lazy(() => import('./pages/member/Documents'));
const MemberPaiement = lazy(() => import('./pages/member/Paiement'));
const MemberAide = lazy(() => import('./pages/member/Aide'));
const MemberCoinAffaires = lazy(() => import('./pages/member/CoinAffaires'));
const MemberBilan = lazy(() => import('./pages/member/Bilan'));
const AdminBilanAssociation = lazy(() => import('./pages/admin/BilanAssociation'));

// Composant de chargement
const LoadingScreen = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    bgcolor="background.default"
  >
    <CircularProgress size={60} />
  </Box>
);

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Routes publiques */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/privacy"        element={<PrivacyPage />} />
          <Route path="/terms"          element={<TermsPage />} />
          <Route path="/help"           element={<HelpPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/login" element={user ? <Navigate to={getDefaultRoute(user)} /> : <LoginPage />} />
          <Route path="/register" element={user ? <Navigate to={getDefaultRoute(user)} /> : <RegisterPage />} />
          <Route path="/genealogie" element={<ArbreGenealogique />} />
          <Route path="/annuaire" element={<AnnuairePage />} />
          <Route path="/coin-affaires" element={<CoinAffairesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<AboutPage />} />
        </Route>

        {/* Routes Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['administrateur', 'president', 'tresorier', 'tresorier_adjoint', 'secretaire_general', 'secretaire_general_adjoint', 'commissaire']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="membres" element={<AdminMembres />} />
          <Route path="cotisations" element={<AdminCotisations />} />
          <Route path="assemblees" element={<AdminAssemblees />} />
          <Route path="tontine" element={<AdminTontine />} />
          <Route path="aides" element={<AdminAides />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="comptes-rendus" element={<CompteRenduListe />} />
          <Route path="depenses" element={<AdminDepenses />} />
          <Route path="recettes" element={<AdminRecettes />} />
          <Route path="rapports" element={<AdminRapports />} />
          <Route path="defaillants" element={<AdminDefaillants />} />
          <Route path="bilan-association" element={<AdminBilanAssociation />} />
          <Route path="rappels"    element={<AdminRappels />} />
          <Route path="messagerie"    element={<AdminMessagerie />} />
          <Route path="anniversaires" element={<AdminAnniversaires />} />
          <Route path="emprunts"       element={<AdminEmprunts />} />
          <Route path="evenements"      element={<AdminEvenements />} />
          <Route path="documents-officiels" element={<AdminDocumentsOfficiels />} />
          <Route path="genealogie"       element={<GenealogiAdmin />} />
          <Route path="comptes-bancaires"  element={<AdminComptesBancaires />} />
          <Route path="settings"   element={<AdminSettings />} />
          <Route path="profile" element={<MemberProfile />} />
          <Route path="/admin/comptes-rendus" element={<CompteRenduListe />} />
          <Route path="/admin/comptes-rendus/nouveau" element={<CompteRenduForm />} />
          <Route path="/admin/comptes-rendus/modifier/:id" element={<CompteRenduForm />} />
          <Route path="debug-comptes-rendus" element={<DebugComptesRendus />} />
          <Route path="/admin/comptes-rendus/:id" element={<CompteRenduDetail />} />
          <Route path="/admin/validation-comptes" element={<ValidationMembres />} />
          <Route path="/admin/validation-annonces" element={<ValidationAnnonces />} />

        </Route>

        {/* Routes Membre */}
        <Route
          path="/member"
          element={
            <ProtectedRoute blockOfficers>
              <MemberLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/member/dashboard" />} />
          <Route path="dashboard" element={<MemberDashboard />} />
          <Route path="profile" element={<MemberProfile />} />
          <Route path="cotisations" element={<MemberCotisations />} />
          <Route path="assemblees" element={<MemberAssemblees />} />
          <Route path="tontine" element={<MemberTontine />} />
          <Route path="documents" element={<MemberDocuments />} />
          <Route path="paiement" element={<MemberPaiement />} />
          <Route path="aide" element={<MemberAide />} />
          <Route path="coin-affaires" element={<MemberCoinAffaires />} />
          <Route path="bilan" element={<MemberBilan />} />
        </Route>

        {/* Route 404 */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <InstallPWA />
    </Suspense>
  );
}

// Fonction pour déterminer la route par défaut selon le rôle
function getDefaultRoute(user) {
  if (!user) return '/login';
  
  switch (user.role) {
    case 'administrateur':
    case 'president':
    case 'tresorier':
    case 'tresorier_adjoint':
    case 'secretaire_general':
    case 'secretaire_general_adjoint':
    case 'commissaire':
      return '/admin/dashboard';
    case 'membre':
    default:
      return '/member/dashboard';
  }
}

export default App;
